import { useState } from "react";
import { Link } from "react-router-dom";
import { Lock, Upload, ShieldCheck, FileText, CreditCard, Building2, CheckCircle2, Users, MapPin, X, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { usePartner, buildRealDocs } from "../PartnerContext";
import { Card, PageHeader, Btn, Pill, StatusBadge, SectionTitle, InfoRow, Field, inputCls } from "../ui";
import { AdminTerritoriesMap } from "@/components/AdminTerritoriesMap";

const LockedRow = ({ label, value, testid }) => (
  <div data-testid={testid} className="flex items-center justify-between gap-4 border-b border-[#F0F2F3] py-2.5 text-sm last:border-b-0">
    <span className="text-stone-500">{label}</span>
    <span className="flex items-center gap-1.5 font-semibold text-navy">
      {value} <Lock className="h-3 w-3 text-stone-400" aria-label="Géré par CozyHome Central" />
    </span>
  </div>
);


const CommunesModal = ({ territory, onClose }) => {
  const [communes, setCommunes] = useState(null);

  useState(() => {
    api.get(`/partner-space/territories/${territory.id}/communes`).then((res) => setCommunes(res.data.communes)).catch(() => setCommunes([]));
    return null;
  });

  return (
    <div data-testid="communes-modal" className="fixed inset-0 z-[100] flex items-center justify-center bg-navy/50 p-4" onClick={onClose}>
      <div className="max-h-[70vh] w-full max-w-md overflow-y-auto rounded-2xl bg-white p-6 shadow-2xl" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between">
          <div>
            <p className="text-xs font-bold uppercase text-petrol">{territory.code}</p>
            <h3 className="text-lg font-bold text-navy">Communes de {territory.name}</h3>
          </div>
          <button data-testid="communes-modal-close" onClick={onClose} className="text-stone-400 hover:text-stone-600"><X className="h-5 w-5" /></button>
        </div>
        <div className="mt-4 space-y-1.5">
          {communes === null ? (
            <p className="text-sm text-stone-400">Chargement…</p>
          ) : communes.length === 0 ? (
            <p className="text-sm text-stone-400">Aucune commune trouvée.</p>
          ) : communes.map((c) => (
            <div key={c.insee_code} data-testid={`commune-row-${c.insee_code}`} className="flex items-center justify-between border-b border-[#F0F2F3] py-1.5 text-sm">
              <span className="text-navy">{c.name}</span>
              <span className="text-stone-400">{(c.postal_codes || [])[0]}</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

const MySectorsSection = ({ territorial }) => {
  const [communesFor, setCommunesFor] = useState(null);
  const [activeTerritory, setActiveTerritory] = useState(null);
  const territories = territorial?.territories || [];

  if (territories.length === 0) {
    return (
      <Card className="mt-4 p-6 text-center text-sm text-stone-500" testid="my-sectors-empty">
        Aucun secteur CozyHome ne vous est encore attribué. Votre secteur apparaîtra ici dès son activation par CozyHome Central.
      </Card>
    );
  }

  return (
    <Card className="mt-4 p-5" testid="my-sectors-section">
      <SectionTitle>Mes secteurs CozyHome</SectionTitle>
      <div className="grid gap-3 sm:grid-cols-2">
        {territories.map((t) => (
          <div key={t.id} data-testid={`my-sector-${t.id}`} className="rounded-xl border border-[#E3E8E9] bg-petrol-bg/40 p-4">
            <div className="flex items-center justify-between">
              <p className="font-mono text-xs font-bold text-petrol">{t.code}</p>
              <Pill tone="sage">Actif</Pill>
            </div>
            <p className="mt-1 text-sm font-bold text-navy">{t.name}</p>
            <p className="mt-1 text-xs text-stone-500">{t.department} · {t.region}</p>
            <p className="mt-1 text-xs text-stone-500">{t.commune_count} commune{t.commune_count > 1 ? "s" : ""} couverte{t.commune_count > 1 ? "s" : ""}</p>
            <button data-testid={`my-sector-view-communes-${t.id}`} onClick={() => setCommunesFor(t)} className="mt-2 flex items-center gap-1 text-xs font-bold text-petrol hover:underline">
              <MapPin className="h-3 w-3" /> Voir les communes
            </button>
          </div>
        ))}
      </div>
      <div className="mt-5">
        <AdminTerritoriesMap
          territories={territories}
          geometriesUrl="/partner-space/territories/geometries"
          fitBounds
          height="h-[420px]"
          onOpenTerritory={setActiveTerritory}
        />
      </div>
      {activeTerritory && (
        <div data-testid="my-sector-info-panel" className="mt-3 rounded-xl border border-[#E3E8E9] bg-white p-4 text-sm">
          <div className="flex items-center justify-between">
            <p className="font-bold text-navy">{activeTerritory.code} — {activeTerritory.name}</p>
            <button onClick={() => setActiveTerritory(null)} className="text-stone-400 hover:text-stone-600"><X className="h-4 w-4" /></button>
          </div>
          <p className="mt-1 text-xs text-stone-500">{activeTerritory.department} · {activeTerritory.region} · {activeTerritory.commune_count} communes · Actif</p>
        </div>
      )}
      {communesFor && <CommunesModal territory={communesFor} onClose={() => setCommunesFor(null)} />}
    </Card>
  );
};

export default function PartnerCompany() {
  const { partner, validated, can, territorial, territorialLoading, backend } = usePartner();
  const [insurance, setInsurance] = useState(validated ? "own" : null);
  if (backend && territorialLoading) {
    return (
      <div data-testid="partner-company-loading" className="flex min-h-[50vh] items-center justify-center text-stone-400">
        <Loader2 className="h-6 w-6 animate-spin" />
      </div>
    );
  }
  const docs = territorial ? buildRealDocs(territorial) : [];
  const compliance = docs.some((d) => d.status === "Refusé")
    ? "Non conforme"
    : docs.some((d) => d.status !== "Validé")
    ? "En vérification"
    : "Conforme";
  const canViewFinancialSettings = can("finance.view_partner_fees");

  return (
    <div data-testid="partner-company">
      <PageHeader eyebrow="Administratif" title="Mon entreprise" sub={canViewFinancialSettings ? "Vos informations, votre abonnement et votre dossier professionnel." : "Informations, documents et échéances utiles à votre mission."} />

      {can("team.view") && <Link to="/partenaire/entreprise/equipe" data-testid="company-team-access-link"><Card className="mb-4 flex items-center justify-between gap-4 border-petrol/20 p-5 transition-colors hover:border-petrol"><div className="flex items-center gap-3"><Users className="h-5 w-5 text-cognac" /><div><p className="font-semibold text-navy">Équipe & accès</p><p className="text-xs text-stone-500">Invitations, rôles, périmètres et permissions.</p></div></div><span className="text-sm font-bold text-petrol">Gérer →</span></Card></Link>}

      <div className="grid gap-4 lg:grid-cols-3">
        {/* Infos entreprise */}
        <Card className={`p-5 ${canViewFinancialSettings ? "lg:col-span-2" : "lg:col-span-3"}`} testid="company-info">
          <SectionTitle right={<Btn variant="ghost" className="px-3.5 py-2 text-xs" testid="company-edit" onClick={() => toast.info("La modification des coordonnées sera connectée dans la phase suivante.")}>Modifier</Btn>}>
            Informations entreprise
          </SectionTitle>
          <div className="grid gap-x-8 sm:grid-cols-2">
            <div>
              <InfoRow label="Raison sociale" value={partner.company} />
              <InfoRow label="Forme juridique" value={partner.legalForm} />
              <InfoRow label="SIRET" value={partner.siret} />
              <InfoRow label="Dirigeant" value={`${partner.firstName} ${partner.lastName}`} />
            </div>
            <div>
              <InfoRow label="Email" value={partner.email} />
              <InfoRow label="Téléphone" value={partner.phone} />
              <InfoRow label="Adresse" value={partner.address} />
              <InfoRow label="Partenaire depuis" value={partner.since} />
            </div>
          </div>
          <div className="mt-4 rounded-xl bg-petrol-bg p-4">
            <p className="text-[11px] font-bold uppercase tracking-[0.2em] text-stone-400">Attribués par CozyHome Central</p>
            <LockedRow label="Code partenaire" value={<span className="font-mono">{partner.code}</span>} testid="company-code" />
            <LockedRow label="Statut de conformité" value={<StatusBadge status={compliance} testid="company-compliance" />} testid="company-compliance-row" />
          </div>
        </Card>

        {/* Abonnement */}
        {canViewFinancialSettings && <Card className="p-5" testid="company-subscription">
          <SectionTitle>Abonnement</SectionTitle>
          <div className="flex items-center justify-between">
            <span className="text-sm text-stone-500">Statut</span>
            <StatusBadge status={partner.subscription.status} testid="company-subscription-status" />
          </div>
          <p className="mt-4 font-display text-3xl font-medium text-navy">{partner.subscription.amount}</p>
          {partner.subscription.isExempt && (
            <p className="mt-1 text-xs font-semibold uppercase tracking-wider text-sage" data-testid="company-subscription-exempt-note">
              Exonération Centrale
            </p>
          )}
          <div className="mt-3">
            <InfoRow label="Souscrit le" value={partner.subscription.startedAt} />
            {partner.subscription.nextBilling && <InfoRow label="Prochaine échéance" value={partner.subscription.nextBilling} />}
          </div>
          <p className="mt-4 text-xs leading-relaxed text-stone-500">
            Les prestations techniques commandées auprès de CozyHome Central sont facturées séparément.
          </p>
          {!partner.subscription.isExempt && (
            <>
              <p className="mt-3 text-[11px] text-stone-400">Statuts possibles : Actif · Impayé · Résilié</p>
              <Btn variant="ghost" className="mt-4 w-full" testid="company-invoices" onClick={() => toast.info("Les factures d'abonnement seront disponibles dans la phase suivante.")}>
                <CreditCard className="h-4 w-4" /> Mes factures d'abonnement
              </Btn>
            </>
          )}
        </Card>}
      </div>

      <MySectorsSection territorial={territorial} />

      {/* Conformité */}
      <Card className="mt-4 p-5" testid="company-compliance-card">
        <SectionTitle
          right={
            <div className="flex items-center gap-2">
              <span className="text-xs text-stone-500">Conformité</span>
              <StatusBadge status={compliance} />
            </div>
          }
        >
          Dossier professionnel
        </SectionTitle>
        {!validated && (
          <div className="mb-4 rounded-xl bg-[#FBF6F2] p-4 text-sm text-stone-600" data-testid="company-onboarding-note">
            <span className="font-bold text-navy">Bienvenue chez CozyHome.</span> Votre abonnement est actif. Complétez maintenant votre dossier professionnel afin de finaliser votre intégration au réseau.
          </div>
        )}
        <ul className="divide-y divide-[#F0F2F3]" data-testid="company-docs">
          {docs.map((d) => (
            <li key={d.id} className="flex flex-col gap-2 py-3 sm:flex-row sm:items-center sm:justify-between">
              <div className="flex items-start gap-3">
                <span className="mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-petrol-tint text-petrol"><FileText className="h-4 w-4" /></span>
                <div>
                  <p className="text-sm font-semibold text-navy">{d.name}</p>
                  <p className="text-xs text-stone-500">
                    {d.date && `Transmis le ${d.date}`}
                    {d.expires && ` · Expire le ${d.expires}`}
                    {d.note && <span className="text-red-600">{d.note}</span>}
                  </p>
                </div>
              </div>
              <div className="flex items-center gap-2 pl-11 sm:pl-0">
                <StatusBadge status={d.status} testid={`doc-status-${d.id}`} />
                {["À fournir", "Refusé", "Expiré"].includes(d.status) && (
                  <Btn variant="soft" className="px-3 py-1.5 text-xs" testid={`doc-upload-${d.id}`} onClick={() => toast.info("Le dépôt de documents sera connecté dans la phase suivante.")}>
                    <Upload className="h-3.5 w-3.5" /> Transmettre
                  </Btn>
                )}
              </div>
            </li>
          ))}
        </ul>
        <p className="mt-3 text-[11px] text-stone-400">
          Statuts : À fournir · En vérification · Validé · Refusé · Expiré. La conformité globale (À compléter / En vérification / Conforme / Non conforme / Document expiré) est déterminée par CozyHome Central.
        </p>
      </Card>

      <div className="mt-4 grid gap-4 lg:grid-cols-2">
        {/* Assurances */}
        <Card className={`p-5 ${canViewFinancialSettings ? "" : "lg:col-span-2"}`} testid="company-insurance">
          <SectionTitle>Assurances</SectionTitle>
          <p className="text-sm text-stone-500">RC Pro et décennale couvrant l'activité de maîtrise d'œuvre sont requises pour rejoindre le réseau.</p>
          <div className="mt-4 grid gap-2 sm:grid-cols-2">
            {[
              { v: "own", label: "J'ai déjà mes assurances", sub: "Je transmets mes attestations." },
              { v: "need", label: "J'ai besoin d'une solution d'assurance", sub: "CozyHome me met en relation avec un partenaire assureur." },
            ].map((o) => (
              <button
                key={o.v}
                data-testid={`insurance-${o.v}`}
                onClick={() => setInsurance(o.v)}
                className={`rounded-xl border-2 p-4 text-left transition-colors ${insurance === o.v ? "border-petrol bg-petrol-tint" : "border-[#E3E8E9] hover:border-petrol/40"}`}
              >
                <p className="flex items-center gap-2 text-sm font-bold text-navy">
                  {insurance === o.v && <CheckCircle2 className="h-4 w-4 text-petrol" />} {o.label}
                </p>
                <p className="mt-1 text-xs text-stone-500">{o.sub}</p>
              </button>
            ))}
          </div>
          {insurance === "need" && (
            <p className="mt-3 rounded-xl bg-[#FBF6F2] p-3 text-xs leading-relaxed text-stone-600" data-testid="insurance-need-note">
              Il s'agit d'une simple mise en relation : elle ne vaut ni acceptation, ni tarif, ni couverture automatique. L'assureur reste seul décisionnaire.
            </p>
          )}
        </Card>

        {/* RIB & contrat */}
        {canViewFinancialSettings && <Card className="p-5" testid="company-bank">
          <SectionTitle>RIB & contrat</SectionTitle>
          <Field label="IBAN professionnel">
            <input className={inputCls} value="" placeholder="FR76 … (à transmettre)" readOnly data-testid="company-iban" />
          </Field>
          <div className="mt-3 flex items-center justify-between rounded-xl border border-[#E3E8E9] p-3">
            <span className="flex items-center gap-2 text-sm font-semibold text-navy"><ShieldCheck className="h-4 w-4 text-petrol" /> Contrat Partenaire CozyHome</span>
            <StatusBadge status={validated ? "Validé" : "À fournir"} testid="company-contract-status" />
          </div>
          <div className="mt-3 flex flex-wrap gap-2">
            <Btn variant="ghost" className="px-3.5 py-2 text-xs" testid="company-contract-download" onClick={() => toast.info("Le contrat sera téléchargeable dans la phase suivante.")}>
              <FileText className="h-3.5 w-3.5" /> {validated ? "Télécharger le contrat" : "Consulter le contrat"}
            </Btn>
            <Btn variant="ghost" className="px-3.5 py-2 text-xs" testid="company-rib-upload" onClick={() => toast.info("Le dépôt du RIB sera connecté dans la phase suivante.")}>
              <Upload className="h-3.5 w-3.5" /> Mettre à jour le RIB
            </Btn>
          </div>
          <p className="mt-4 flex items-start gap-2 text-[11px] leading-relaxed text-stone-400">
            <Building2 className="mt-0.5 h-3.5 w-3.5 shrink-0" /> Le statut de conformité, le secteur attribué et le code partenaire sont gérés exclusivement par CozyHome Central.
          </p>
        </Card>}
      </div>
    </div>
  );
}
